import { createFileRoute, Outlet } from "@tanstack/react-router";
import type { LiteClient } from "algoliasearch/lite";
import { TicketsPlaneIcon, X, ZapIcon } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Configure, InstantSearch } from "react-instantsearch";
import { z } from "zod";

import { isUpclaim } from "@upclaim/sdk/src/admin-roles";

import { ResizablePane } from "@/components/resizable-pane";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { BlockedClaimsSection } from "@/features/reporting/components/blocked-claims-section";
import { useClaimCounters } from "@/features/search/api/use-claim-counters";
import { useSearchClient } from "@/features/search/api/use-scoped-search-key";
import { FilterTabs } from "@/features/search/components/filter-tabs";
import { FlightDateRangeProvider } from "@/features/search/components/flight-date-range-context";
import { FLIGHT_FACETS } from "@/features/search/components/flight-filters";
import { RefinementCardGroup } from "@/features/search/components/refinement-card-group";
import { SearchClaimTable } from "@/features/search/components/search-claim-table";
import { SearchFilterBar } from "@/features/search/components/search-filter-bar";
import { SearchPagination } from "@/features/search/components/search-pagination";
import { SyncPnrDialog } from "@/features/search/components/sync-pnr-dialog";
import {
  ArchivedFilters,
  BlockedFilters,
  ProcedureFilters,
} from "@/features/search/components/tab-filter-panels";
import { VirtualRefinements } from "@/features/search/components/virtual-refinements";
import { buildFilterTabs, getInitialSearchFilters } from "@/features/search/lib/filter-tabs";
import { createPreviewAwareRouting } from "@/features/search/lib/preview-routing";
import { CLAIM_PREVIEW_TABS, ClaimPreviewPanel } from "@/features/tasks/components/claim-preview-panel";
import { useCurrentUser } from "@/hooks/use-current-user";
import { config } from "@/lib/config";
import { cn } from "@/lib/utils";

const HITS_PER_PAGE = 20;
/** « Charger plus » grows the current page (hitsPerPage) instead of paginating. */
const MAX_LOADED_PAGES = 10;

// Our own params, kept out of Algolia's URL sync: the claim pane, and the file explorer opened over the list.
const PREVIEW_PARAMS = ["claimId", "claimTab", "folder", "file"] as const;

// Every refinement attribute whose UI mounts lazily (collapsed "Filtres" bar in
// SearchClaimTable, tab-gated Procedure/Archived panels). Virtual widgets for these must stay
// mounted at the InstantSearch root, otherwise a shared URL's ?claim[refinementList][…] filters
// are dropped at the first search. Keep in sync with the RefinementCombobox/RefinementCardGroup
// attributes of those panels.
const LAZY_REFINEMENT_ATTRIBUTES = [
  // SearchClaimTable "Filtres" bar
  "travelAgencyOffice.travelAgency.name",
  "travelAgencyOffice.amadeusOfficeId",
  "agent.email",
  "travelAgencyCompany.displayName",
  "teamName",
  "assignedAdmin.fullName",
  "subAccount",
  "travelAgencyCompany.bluebizIdentifier",
  "Tags",
  // ProcedureFilters / ArchivedFilters (tab-gated)
  "procedureJuridiction.name",
  "groupNames",
  "numeroRG",
  "enrolmentStatus",
  "procedureType",
  "isInProcedureWave",
  "isBusinessTravel",
  // FlightFilters (« Vol » header) — facet panels mount lazily inside the popover
  ...FLIGHT_FACETS,
] as const;

// Loose so the InstantSearch routing params (?claim[...]) survive our own navigations.
// NB: the pane param is claimId, NOT claim — the Algolia index is named "claim",
// so a ?claim scalar would collide with InstantSearch's claim[...] namespace in qs.
const claimsSearchSchema = z.looseObject({
  claimId: z.string().optional().catch(undefined),
  claimTab: z.enum(CLAIM_PREVIEW_TABS).optional().catch(undefined),
});

/**
 * The claims list, as a layout: what opens over it (the file explorer) is a child route, so the list —
 * Algolia state, results, scroll — stays mounted underneath and is intact on return.
 */
export const Route = createFileRoute("/_authed/_dashboard/claims/_list")({
  validateSearch: claimsSearchSchema,
  component: ClaimsPage,
});

function ClaimsPage() {
  const [syncPnrOpen, setSyncPnrOpen] = useState(false);
  const { client } = useSearchClient();
  // Only what this page reads: the router hands a new search object on every navigation (the file explorer
  // opening over the list, InstantSearch writing its state), which would re-render the whole list each time.
  const search = {
    claimId: Route.useSearch({ select: ({ claimId }) => claimId }),
    claimTab: Route.useSearch({ select: ({ claimTab }) => claimTab }),
  };
  const navigate = Route.useNavigate();

  // Kept mounted through the close transition, cleared on transitionend.
  const [renderedClaimId, setRenderedClaimId] = useState<string | undefined>(search.claimId);
  useEffect(() => {
    if (search.claimId) {
      setRenderedClaimId(search.claimId);
    }
  }, [search.claimId]);

  const closeClaimPreview = () => {
    navigate({
      search: (prev) => ({ ...prev, claimId: undefined, claimTab: undefined }),
      replace: true,
      resetScroll: false,
    });
  };

  /** Vue parity (Claims.vue handleClaimClick) — re-clicking the open claim closes the pane. */
  const toggleClaimPreview = (claimId: string) => {
    if (search.claimId === claimId) {
      closeClaimPreview();
      return;
    }
    // Pushed (not replaced) so the browser back button closes the pane.
    navigate({
      search: (prev) => ({ ...prev, claimId, claimTab: "overview" as const }),
      resetScroll: false,
      replace: false,
    });
  };

  return (
    <div className="px-4 pt-8 sm:px-6 lg:px-12">
      <SyncPnrDialog open={syncPnrOpen} onOpenChange={setSyncPnrOpen} />

      <div className="text-left sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="flex items-center gap-2 text-xl font-semibold text-gray-900">
            <TicketsPlaneIcon className="h-5 w-5 flex-none text-stone-400" aria-hidden="true" />
            Claims
          </h1>
          <p className="mt-2 text-sm text-gray-600">Voir et rechercher parmis toutes les claims en cours.</p>
        </div>
        <div className="mt-4 sm:ml-6 sm:mt-0 sm:flex-none">
          <Button onClick={() => setSyncPnrOpen(true)}>
            <ZapIcon data-icon="inline-start" />
            Synchroniser un PNR
          </Button>
        </div>
      </div>

      {client ? (
        <ClaimsSearch
          client={client}
          onClaimClick={toggleClaimPreview}
          activeClaimId={search.claimId ?? null}
        />
      ) : (
        <ClaimsSearchSkeleton />
      )}

      {/* Right slide-over — claim "plugin" preview driven by the ?claimId search param
          (Vue plugin.layout.vue parity: slide transition + floating circular close button).
          The container stays mounted and slides via transform so BOTH open and close
          animate; the pane content unmounts only after the close transition ends. */}
      {createPortal(
        <div
          className={cn(
            "pointer-events-none fixed top-0 right-0 z-40 flex h-screen max-w-full pl-10 transition-transform duration-300 ease-in-out",
            search.claimId ? "translate-x-0" : "translate-x-full",
          )}
          onTransitionEnd={(event) => {
            if (event.target === event.currentTarget && !search.claimId) {
              setRenderedClaimId(undefined);
            }
          }}
        >
          {renderedClaimId ? (
            <ResizablePane
              key={renderedClaimId}
              storageKey="claims-preview-pane-width"
              minWidth={480}
              defaultWidth={552}
              className="pointer-events-auto relative h-full"
            >
              {/* Floating close button hanging at the pane's top-left edge (Vue plugin.layout.vue). */}
              <div className="absolute -left-4 top-0 z-20 -ml-6 flex pt-4 pr-2 sm:-ml-8 sm:pr-4">
                <button
                  type="button"
                  onClick={closeClaimPreview}
                  className="rounded-full border border-gray-300 bg-white text-gray-400 shadow-md hover:text-gray-500 focus:outline-none"
                >
                  <span className="sr-only">Fermer le panneau</span>
                  <div className="p-2">
                    <X className="h-6 w-6" aria-hidden="true" />
                  </div>
                </button>
              </div>
              <div className="flex h-full flex-col overflow-hidden border-l border-gray-200 bg-white shadow-xl">
                <ClaimPreviewPanel
                  claimId={renderedClaimId}
                  activeTab={search.claimTab ?? "overview"}
                  onTabChange={(tab) =>
                    navigate({
                      search: (prev) => ({ ...prev, claimTab: tab }),
                      replace: true,
                      resetScroll: false,
                    })
                  }
                  onClose={closeClaimPreview}
                />
              </div>
            </ResizablePane>
          ) : null}
        </div>,
        document.body,
      )}
    </div>
  );
}

/** Skeleton shown while the scoped Algolia key loads (search UI is gated on it). */
function ClaimsSearchSkeleton() {
  return (
    <div className="mt-8 flex flex-col">
      <div className="my-6 flex gap-x-8 border-b border-gray-200 pb-4">
        <Skeleton className="h-8 w-40" />
        <Skeleton className="h-8 w-40" />
        <Skeleton className="h-8 w-40" />
      </div>
      <Skeleton className="mb-8 h-24 w-72" />
      <Skeleton className="h-96 w-full" />
    </div>
  );
}

interface ClaimsSearchProps {
  client: LiteClient;
  onClaimClick: (claimId: string) => void;
  activeClaimId: string | null;
}

function ClaimsSearch({ client, onClaimClick, activeClaimId }: ClaimsSearchProps) {
  const { user, roles } = useCurrentUser();
  const isLawyer = roles.includes("LAWYER");
  const isAdmin = roles.some(isUpclaim);

  const tabs = useMemo(
    () => buildFilterTabs({ currentAdminEmail: user?.email, isLawyer, isAdmin }),
    [user?.email, isLawyer, isAdmin],
  );
  const [currentTabIndex, setCurrentTabIndex] = useState(0);
  // Role-based initial filters, independent of the highlighted tab (which never emits on mount).
  const [searchFilters, setSearchFilters] = useState(() => getInitialSearchFilters(isLawyer));
  const [loadedPages, setLoadedPages] = useState(1);

  const { data: counters } = useClaimCounters(client);
  const routing = useMemo(() => createPreviewAwareRouting(PREVIEW_PARAMS), []);

  const activeTab = tabs[currentTabIndex];

  return (
    <div className="mt-8 flex flex-col">
      {/* Flight date range shared by the « Vol » header popover and the filter bar
          chips. Mounted *above* InstantSearch: it is plain local state, and
          nesting it inside tied its lifecycle to a third-party widget tree that
          re-mounts on its own schedule — consumers could then render after the
          provider had gone. */}
      <FlightDateRangeProvider>
        <InstantSearch
          searchClient={client}
          indexName={config.ALGOLIA_CLAIM_INDEX}
          routing={routing}
          future={{ preserveSharedStateOnUnmount: true }}
        >
          <Configure filters={searchFilters} hitsPerPage={HITS_PER_PAGE * loadedPages} />
          <VirtualRefinements attributes={LAZY_REFINEMENT_ATTRIBUTES} />

          <FilterTabs
            className="my-6"
            tabs={tabs}
            currentIndex={currentTabIndex}
            counters={counters}
            onSelect={(tab, index) => {
              setCurrentTabIndex(index);
              setSearchFilters(tab.filter);
            }}
          />

          <div className="flex gap-x-8 pb-8 pt-4 text-left">
            <RefinementCardGroup attribute="claimLegalStepName" attributeName="Étape juridique" size="lg" />
          </div>
          <div>
            <SearchFilterBar className="py-4" />
          </div>

          {activeTab?.advancedFilter === "procedure" ? <ProcedureFilters /> : null}
          {activeTab?.advancedFilter === "archived" ? <ArchivedFilters /> : null}
          {activeTab?.advancedFilter === "blocked" ? <BlockedFilters /> : null}

          <SearchClaimTable onClaimClick={onClaimClick} activeClaimId={activeClaimId} />

          <div className="relative py-10">
            <SearchPagination
              pageSize={HITS_PER_PAGE * loadedPages}
              onLoadMore={loadedPages < MAX_LOADED_PAGES ? () => setLoadedPages(loadedPages + 1) : undefined}
            />
          </div>

          {/* Photo à date du stock bloqué, sous la liste : on lit d'abord ses dossiers, puis la
              forme du blocage. Hors Algolia — la source est le datalake, donc les filtres de
              recherche au-dessus ne s'y appliquent pas. */}
          {activeTab?.advancedFilter === "blocked" ? (
            <div className="pb-10">
              <BlockedClaimsSection />
            </div>
          ) : null}
        </InstantSearch>
      </FlightDateRangeProvider>
      <Outlet />
    </div>
  );
}
